/**
 * PreUploadDetailsForm — captures human-readable identification BEFORE
 * the upload starts.
 *
 * Production bug it solves (Feb 8, 2026): cards across the platform
 * showed "NO20260503-164104-000572.mov" — unusable for humans. Once
 * an instructor uploads multiple clips a day, raw filenames become
 * impossible to triage. Operator directive (verbatim):
 *
 *   "Show: Title, Duration, Upload date/time, Camera label,
 *    Motion detected status, Processing status."
 *
 * This component covers Title + Notes + Camera label. Motion-
 * detected status + camera enumeration are deferred (separate AI
 * pipeline / multi-cam infra needed).
 *
 * Smart default title:
 *   "{pupilName} — {focusLabel} — {DD MMM HH:mm}"
 *   falls back to "Lesson review — {DD MMM HH:mm}" when no pupil.
 *
 * Mounts inline above the upload zone after a file has been picked
 * and the user is past the iPhone-readability gate. The user can
 * edit any field, then taps "Continue → upload" to start. They can
 * also tap "Cancel" to discard the picked file and re-pick.
 */
import { useEffect, useState } from "react";
import { Button } from "../ui/button";
import { Sparkles, X, Camera, FileText } from "lucide-react";

const MAX_TITLE_CHARS = 80;
const MAX_NOTES_CHARS = 500;
const MAX_CAMERA_CHARS = 40;


function _formatDateNow() {
  const d = new Date();
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun",
                  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = months[d.getMonth()];
  const hh = String(d.getHours()).padStart(2, "0");
  const mi = String(d.getMinutes()).padStart(2, "0");
  return `${dd} ${mm} ${hh}:${mi}`;
}


export function smartDefaultTitle({ pupilName, focusLabel } = {}) {
  const stamp = _formatDateNow();
  const pn = (pupilName || "").trim();
  const fl = (focusLabel || "").trim();
  if (pn && fl) return `${pn} — ${fl} — ${stamp}`;
  if (pn) return `${pn} — Lesson review — ${stamp}`;
  if (fl) return `${fl} — ${stamp}`;
  return `Lesson review — ${stamp}`;
}


export default function PreUploadDetailsForm({
  file,
  pupilName = null,
  focusLabel = null,
  onContinue,
  onCancel,
  busy = false,
}) {
  const [title, setTitle] = useState(() =>
    smartDefaultTitle({ pupilName, focusLabel })
  );
  const [notes, setNotes] = useState("");
  const [cameraLabel, setCameraLabel] = useState("");

  // Refresh the smart default if the surrounding context changes
  // (rare — e.g. user edits the focus area on a separate panel).
  useEffect(() => {
    setTitle((current) => {
      // Only auto-update if the user hasn't typed yet (default still
      // matches a freshly-computed smart default).
      const expected = smartDefaultTitle({ pupilName, focusLabel });
      if (current === expected) return current;
      return current;
    });
  }, [pupilName, focusLabel]);

  const sizeMb = file?.size
    ? (file.size / (1024 * 1024)).toFixed(file.size < 100 * 1024 * 1024 ? 1 : 0)
    : null;

  const handleContinue = () => {
    if (busy) return;
    const cleanTitle = title.trim().slice(0, MAX_TITLE_CHARS);
    const cleanNotes = notes.trim().slice(0, MAX_NOTES_CHARS);
    const cleanCamera = cameraLabel.trim().slice(0, MAX_CAMERA_CHARS);
    onContinue?.({
      display_title: cleanTitle ||
        smartDefaultTitle({ pupilName, focusLabel }),
      notes: cleanNotes || null,
      camera_label: cleanCamera || null,
    });
  };

  return (
    <div
      data-testid="lr-pre-upload-form"
      className="rounded-2xl border border-indigo-400/30 bg-indigo-500/[0.06] p-4 mb-3"
    >
      <div className="flex items-start gap-3">
        <div className="w-8 h-8 rounded-lg bg-indigo-500/15 border border-indigo-400/30 flex items-center justify-center flex-none">
          <Sparkles className="w-4 h-4 text-indigo-300" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[13px] font-extrabold text-indigo-100">
            Name this review
          </p>
          <p className="text-[11.5px] text-indigo-100/70 mt-0.5">
            {file?.name || "—"}
            {sizeMb ? ` · ${sizeMb} MB` : ""}
          </p>

          <div className="mt-3 space-y-2.5">
            <label className="block">
              <span className="text-[10px] uppercase tracking-widest font-extrabold text-indigo-200/70">
                Title
              </span>
              <input
                data-testid="lr-pre-upload-title"
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value.slice(0, MAX_TITLE_CHARS))}
                maxLength={MAX_TITLE_CHARS}
                placeholder="e.g. Rachel — Roundabout Practice"
                className="mt-1 w-full bg-zinc-900/60 border border-zinc-700 rounded-lg px-3 py-2 text-[13px] text-zinc-100 placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:ring-indigo-400/60"
              />
            </label>

            <label className="block">
              <span className="text-[10px] uppercase tracking-widest font-extrabold text-indigo-200/70 flex items-center gap-1.5">
                <Camera className="w-3 h-3" /> Camera / view (optional)
              </span>
              <input
                data-testid="lr-pre-upload-camera"
                type="text"
                value={cameraLabel}
                onChange={(e) => setCameraLabel(e.target.value.slice(0, MAX_CAMERA_CHARS))}
                maxLength={MAX_CAMERA_CHARS}
                placeholder="e.g. Front road camera"
                className="mt-1 w-full bg-zinc-900/60 border border-zinc-700 rounded-lg px-3 py-2 text-[13px] text-zinc-100 placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:ring-indigo-400/60"
              />
            </label>

            <label className="block">
              <span className="text-[10px] uppercase tracking-widest font-extrabold text-indigo-200/70 flex items-center gap-1.5">
                <FileText className="w-3 h-3" /> Notes (optional)
              </span>
              <textarea
                data-testid="lr-pre-upload-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value.slice(0, MAX_NOTES_CHARS))}
                maxLength={MAX_NOTES_CHARS}
                rows={2}
                placeholder="Anything you want to remember about this clip…"
                className="mt-1 w-full bg-zinc-900/60 border border-zinc-700 rounded-lg px-3 py-2 text-[13px] text-zinc-100 placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:ring-indigo-400/60 resize-none"
              />
              <span className="text-[10px] text-zinc-500 mt-0.5 inline-block">
                {notes.length}/{MAX_NOTES_CHARS}
              </span>
            </label>
          </div>

          <div className="flex flex-wrap gap-2 mt-3">
            <Button
              data-testid="lr-pre-upload-continue"
              onClick={handleContinue}
              disabled={busy}
              className="h-9 px-4 rounded-md bg-indigo-400 hover:bg-indigo-300 text-[#0a0420] text-[12px] font-extrabold"
            >
              Continue → upload
            </Button>
            <button
              type="button"
              data-testid="lr-pre-upload-cancel"
              onClick={onCancel}
              disabled={busy}
              className="h-9 px-3 rounded-md text-zinc-300 text-[12px] font-semibold hover:bg-white/[0.05] inline-flex items-center gap-1"
            >
              <X className="w-3 h-3" /> Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
